import { useState } from "react";
import { Button, Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { type GlSyncError } from "~/pages/FundAccounting/utils/columnDefs";
import {
  describeBatchSyncStatus,
  GlSyncPhase,
  type SubSyncStatus,
} from "~/pages/FundAccounting/utils/glSyncStatus";
import { generateDataMapsUrl } from "~/pages/FundAccounting/utils/helpers";
import { SyncErrorModal } from "../../../SyncErrorModal";
import { BatchRowStatus as BatchStatusEnum } from "../utils";
import EntitySettingsNotRestrictModal from "./EntitySettingsNotRestrictModal";
import StatusErrorModal from "./StatusErrorModal";
import * as styles from "./StatusRenderer_vanilla.css";

export type Props = {
  value: {
    status: BatchStatusEnum;
    positionId: number;
    rawSyncStatus?: SubSyncStatus;
    syncError?: GlSyncError | null;
  };
  context: {
    arenaName: string;
    arenaSubdomain: string;
    entityId: number;
    entitySettingsNotRestrict: boolean;
  };
};

const BatchStatusRenderer = (params: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [showSyncErrorModal, setShowSyncErrorModal] = useState(false);
  const { positionId, status, rawSyncStatus, syncError } = params.value;
  // Mirrors the entity-level cell: SyncErrorModal navigates here only for the
  // "Mapping error" label; other labels (e.g. duplicate batch) ignore it.
  const mappingsLink = generateDataMapsUrl(params.context.arenaSubdomain);
  if (status === BatchStatusEnum.NOT_READY) {
    return (
      <>
        <Flex alignItems="center" gap={8} className={styles.cell}>
          <Icon type="info-circle" variant="outline" color={Colors.Danger600} />

          <Text className={styles.dangerText} marginBottom="xs">
            {BatchStatusEnum.NOT_READY}
          </Text>
          <Button
            variant="link"
            onClick={() => {
              setShowModal(true);
            }}
          >
            Details
          </Button>
        </Flex>
        {params.context.entitySettingsNotRestrict ? (
          <EntitySettingsNotRestrictModal
            arenaName={params.context.arenaName}
            arenaSubdomain={params.context.arenaSubdomain}
            entityId={params.context.entityId}
            onClose={() => setShowModal(false)}
            showModal={showModal}
          />
        ) : (
          <StatusErrorModal
            arenaName={params.context.arenaName}
            arenaSubdomain={params.context.arenaSubdomain}
            onClose={() => setShowModal(false)}
            showModal={showModal}
            positionId={positionId}
          />
        )}
      </>
    );
  }

  // NOT_READY is handled above, so pre-sync states keep their existing display.
  const batchDisplay = describeBatchSyncStatus(rawSyncStatus);
  if (batchDisplay) {
    if (batchDisplay.showSpinner) {
      return (
        <Flex alignItems="center" gap={8} className={styles.cell}>
          <LoadingIndicator size="small" />
          <Text className={styles.neutralText} marginBottom="xs">
            {batchDisplay.label}
          </Text>
        </Flex>
      );
    }
    if (batchDisplay.phase === GlSyncPhase.Failed) {
      return (
        <>
          <Flex alignItems="center" gap={8} className={styles.cell}>
            <Icon type="info-circle" variant="outline" color={Colors.Danger600} />
            <Text className={styles.dangerText} marginBottom="xs">
              {batchDisplay.label}
            </Text>
            {syncError && (
              <Button variant="link" onClick={() => setShowSyncErrorModal(true)}>
                View
              </Button>
            )}
          </Flex>
          {syncError && (
            <SyncErrorModal
              showSyncErrorModal={showSyncErrorModal}
              closeSyncErrorModal={() => setShowSyncErrorModal(false)}
              label={syncError.label}
              modalMessage={syncError.modalMessage}
              errorMessages={syncError.errorMessages}
              mappingUrl={mappingsLink}
            />
          )}
        </>
      );
    }
    return (
      <Flex alignItems="center" gap={8} className={styles.cell}>
        <Icon type="check" color={Colors.Informative600} />
        <Text className={styles.informativeText} marginBottom="xs">
          {batchDisplay.label}
        </Text>
      </Flex>
    );
  }

  if (status === BatchStatusEnum.FAILED) {
    return (
      <>
        <Flex alignItems="center" gap={8} className={styles.cell}>
          <Icon type="info-circle" variant="outline" color={Colors.Danger600} />
          <Text className={styles.dangerText} marginBottom="xs">
            {BatchStatusEnum.FAILED}
          </Text>
          {syncError && (
            <Button variant="link" onClick={() => setShowSyncErrorModal(true)}>
              View
            </Button>
          )}
        </Flex>
        {syncError && (
          <SyncErrorModal
            showSyncErrorModal={showSyncErrorModal}
            closeSyncErrorModal={() => setShowSyncErrorModal(false)}
            label={syncError.label}
            modalMessage={syncError.modalMessage}
            errorMessages={syncError.errorMessages}
            mappingUrl={mappingsLink}
          />
        )}
      </>
    );
  }
  // when some batches are in complete status, slideout will refetch batch data with the loading state
  // during the refetch, the status needs to remain as processing
  if (status === BatchStatusEnum.PROCESSING || status === BatchStatusEnum.COMPLETE) {
    return (
      <Flex alignItems="center" gap={8} className={styles.cell}>
        <Icon type="hourglass-start" color={Colors.Neutral600} />

        <Text className={styles.neutralText} marginBottom="xs">
          {BatchStatusEnum.PROCESSING}
        </Text>
      </Flex>
    );
  }
  return (
    <Flex alignItems="center" gap={8} className={styles.cell}>
      <Icon type="check" color={Colors.Informative600} />

      <Text className={styles.informativeText} marginBottom="xs">
        {BatchStatusEnum.READY}
      </Text>
    </Flex>
  );
};

export default BatchStatusRenderer;
